/**
 * /trip/:token — what an invited person sees.
 *
 * TWO THINGS ARE REQUIRED TO GET IN: the link, and being signed in as the
 * address the invitation names. This page's job is mostly to make that second
 * requirement survivable — an invited person who has never registered lands
 * here first, and everything they need to become someone who can open the trip
 * is on this screen rather than somewhere they have to go and find.
 *
 * The three states are deliberately distinct:
 *
 *   SIGN_IN_REQUIRED  nobody is signed in. Offer Register and Sign in, and say
 *                     which address (masked) the invitation is for, because
 *                     people have more than one and "sign in" alone sends them
 *                     to guess.
 *   WRONG_ACCOUNT     somebody is signed in, and it is not the invited person.
 *                     Telling them to "sign in" here is a loop they cannot
 *                     escape — they ARE signed in. They need to sign out.
 *   ALLOWED           show the trip.
 *
 * Read-only, without exception. Nothing here changes the trip, and finalising
 * belongs to the person who created it.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import PageTemplate from '../../components/PageTemplate';
import TripPreview from '../../components/TripPreview';
import TripComments from '../../components/TripComments';
import { LoginForm } from '../../components/Login';
import { RegisterForm } from '../../components/Register';

import { sendEmailVerification } from 'firebase/auth';

import { useAuth } from '../../../context/Auth.context';
import { auth } from '../../../config/firebase';
import { inviteGateState } from '../../../utils/tripSharing';
import { markInviteViewed, readInvite, readInviteGate } from '../../../utils/tripInvites';

export default function SharedTripPage() {
  const { token } = useParams();
  const { user, onLogout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null); // the public, masked half
  const [invite, setInvite] = useState(null); // the real thing, once allowed
  const [openLogin, setOpenLogin] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [resent, setResent] = useState('');

  const userEmail = user?.email || '';
  const emailVerified = !!user?.emailVerified;

  useEffect(() => {
    // `cancelled` is load-bearing, not hygiene. `user` starts null and is
    // restored asynchronously, so every visit runs this twice: once signed
    // out, whose read is refused, and once signed in, which succeeds. Without
    // the guard the refused one can resolve LAST and overwrite the good
    // result, dropping a correctly-invited guest onto the "wrong account"
    // dead end until they reload.
    let cancelled = false;

    (async () => {
      setLoading(true);

      // Always readable, signed in or not, and reveals only a masked address.
      // Without it the sign-in screen could not name the account to use.
      const foundGate = await readInviteGate(token);
      if (cancelled) return;
      setGate(foundGate);

      if (!foundGate) {
        setInvite(null);
        setLoading(false);
        return;
      }

      // Refused by the rules unless the caller is signed in, verified, and at
      // the invited address. A refusal is the ordinary case, not a fault.
      const foundInvite = await readInvite(token);
      if (cancelled) return;
      setInvite(foundInvite);
      setLoading(false);

      // Courtesy write, after render is already decided.
      if (foundInvite) markInviteViewed(token);
    })();

    return () => {
      cancelled = true;
    };
    // Re-runs when the account changes, so registering or signing in on this
    // screen opens the trip without a reload — the whole point of mounting the
    // auth dialogs here rather than sending people off to the menu.
  }, [token, userEmail, emailVerified]);

  const handleResend = useCallback(async () => {
    try {
      if (auth.currentUser) await sendEmailVerification(auth.currentUser);
      setResent('Sent. Check your inbox, then reload this page.');
    } catch {
      setResent('We could not send that just now. Try again in a minute.');
    }
  }, []);

  if (loading) {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
          <CircularProgress aria-label="Loading trip" />
        </Box>
      </PageTemplate>
    );
  }

  // Unknown and withdrawn are deliberately the same message: telling a holder
  // which one it was tells them whether the token was ever real.
  if (!gate) {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ maxWidth: 640, mx: 'auto', px: 3, py: 6, textAlign: 'center' }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            This trip link is no longer active
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            The link may have expired, or the person organising the trip may have removed it. Ask
            them to send you a new one.
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Back to Vhakacha
          </Button>
        </Box>
      </PageTemplate>
    );
  }

  const state = inviteGateState({ canRead: !!invite, userEmail, emailVerified });

  const tripWord = gate.tripType === 'BOOKING' ? 'booking' : 'trip';

  /* ── not signed in ── */
  if (state === 'SIGN_IN_REQUIRED') {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 3, py: 6 }}>
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <LockOutlinedIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />

            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              You have been invited to a {tripWord}
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 1 }}>
              Vhakacha {tripWord}s are private. Create an account, or sign in, to open this one.
            </Typography>

            {gate.emailMasked && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This invitation is for <strong>{gate.emailMasked}</strong> — use that address.
              </Typography>
            )}

            <Stack spacing={1.5} sx={{ maxWidth: 320, mx: 'auto' }}>
              <Button variant="contained" size="large" onClick={() => setOpenRegister(true)}>
                Create an account
              </Button>
              <Button variant="outlined" size="large" onClick={() => setOpenLogin(true)}>
                I already have one
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
              Registering takes an email address and a password. You will be asked to confirm the
              address before the {tripWord} opens.
            </Typography>
          </Paper>
        </Box>

        {/* Mounted here rather than reached through the menu: an invited person
            sent straight to a sign-in screen should not have to go and find the
            way in. Both dialogs pre-fill nothing but the masked hint above,
            because the real address is not readable until they are signed in. */}
        <LoginForm open={openLogin} setOpen={setOpenLogin} />
        <RegisterForm open={openRegister} setOpen={setOpenRegister} />
      </PageTemplate>
    );
  }

  /* ── signed in, right address, not yet verified ── */
  if (state === 'EMAIL_UNVERIFIED') {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 3, py: 6 }}>
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <LockOutlinedIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />

            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Confirm your email address
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              We sent a link to <strong>{userEmail}</strong>. Open it, then come back here.
            </Typography>

            {/* Not bureaucracy. Anyone can register an address they do not own,
                so an unconfirmed address proves nothing — and this {tripWord}
                is private to the person who actually holds it. */}
            <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
              Trips are only shown to the person they were shared with, so we need to know this
              address is yours.
            </Alert>

            {resent && (
              <Alert severity="success" sx={{ textAlign: 'left', mb: 2 }}>
                {resent}
              </Alert>
            )}

            <Stack spacing={1.5} sx={{ maxWidth: 320, mx: 'auto' }}>
              <Button variant="contained" size="large" onClick={() => window.location.reload()}>
                I have confirmed it
              </Button>
              <Button variant="text" onClick={handleResend}>
                Send the link again
              </Button>
            </Stack>
          </Paper>
        </Box>
      </PageTemplate>
    );
  }

  /* ── signed in as somebody else ── */
  if (state === 'WRONG_ACCOUNT') {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ maxWidth: 560, mx: 'auto', px: 3, py: 6 }}>
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <LockOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1.5 }} />

            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              This invitation is for a different account
            </Typography>

            <Typography color="text.secondary" sx={{ mb: 2 }}>
              You are signed in as <strong>{userEmail}</strong>
              {gate.emailMasked ? <>, but this {tripWord} was shared with <strong>{gate.emailMasked}</strong></> : null}.
            </Typography>

            <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
              If that is also you, sign out and sign back in with the other address. If it is not,
              ask whoever organised the {tripWord} to invite you at{' '}
              <strong>{userEmail}</strong> instead.
            </Alert>

            <Stack spacing={1.5} sx={{ maxWidth: 320, mx: 'auto' }}>
              <Button variant="contained" size="large" onClick={() => onLogout()}>
                Sign out
              </Button>
              <Button component={Link} to="/" variant="text">
                Back to Vhakacha
              </Button>
            </Stack>
          </Paper>
        </Box>
      </PageTemplate>
    );
  }

  /* ── allowed ── */
  const snapshot = invite.snapshot;
  const finalised = !!snapshot?.finalized;

  return (
    <PageTemplate>
      <Toolbar />

      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, pb: 6 }}>
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {invite.name}, you have been invited to this {tripWord}
            </Typography>
            <Chip
              size="small"
              color={finalised ? 'success' : 'default'}
              variant={finalised ? 'filled' : 'outlined'}
              label={finalised ? 'Finalised' : 'Being planned'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            You can read everything here and leave comments or suggestions. Only the person who
            created this {tripWord} can change it.
          </Typography>
        </Paper>

        {!finalised && snapshot?.tripType === 'ITINERARY' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This trip is still being planned, so details may change. You will get the final copy when
            it is settled.
          </Alert>
        )}

        <TripPreview snapshot={snapshot} />

        <Box sx={{ mx: -2 }}>
          <TripComments
            threadId={invite.threadId}
            inviteId={invite.inviteId}
            authorName={invite.name}
            canModerate={false}
            readOnly={finalised}
          />
        </Box>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button component={Link} to="/" variant="text">
            Explore Vhakacha
          </Button>
        </Box>
      </Box>
    </PageTemplate>
  );
}
